import * as THREE from 'three';
import { simToWorld } from '@somnhold/render';
import type { BattleState, Enemy } from '@somnhold/sim';
import { t } from '../i18n.ts';
import { h, setHidden, setStyle, setText, viewH, viewW } from '../ui/dom.ts';

/** A chevron pointing down; turned to point at a place off screen (the crypt's arrows). */
const CHEVRON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 8l7 8 7-8"/></svg>';

/** At most this many arrows, the nearest first. */
const MAX = 3;

/** The golems are loaded and wait for the field: the nightmares still standing. */
export const lastFoes = (s: BattleState): Enemy[] => {
  const o = s.objective;
  if (!o || !s.rules?.clearToFinish || s.result !== 'running' || o.progress < o.total - 1) return [];
  return s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall');
};

/**
 * An incursion that waits on its last nightmares: an arrow over each one, or held at the screen's edge
 * pointing to it (one left in the dark off the clearing kept a group looking at an empty field).
 */
export class LastFoes {
  readonly el = h('div.dg-hud.pl-ways');
  private els: { el: HTMLElement; arrow: HTMLElement; txt: HTMLElement }[] = [];
  private v = new THREE.Vector3();
  private count = -1;
  private label = '';

  constructor(
    private s: () => BattleState,
    private me: string,
  ) {}

  place(positions: Map<number, THREE.Vector3>, camera: THREE.Camera): void {
    const s = this.s();
    const mine = s.heroes.find((x) => x.pid === this.me);
    const from = mine ?? { x: 0, y: 0 };
    const all = lastFoes(s);
    // One count over the nearest arrow: each saying "one left" while five stood read as five last ones.
    if (all.length !== this.count) {
      this.count = all.length;
      this.label = all.length === 1 ? t('inc.wayFoe') : t('inc.wayFoes', { n: all.length });
    }
    const foes = all
      .map((e) => ({ e, d: (e.x - from.x) ** 2 + (e.y - from.y) ** 2 }))
      .sort((a, b) => a.d - b.d || a.e.id - b.e.id)
      .slice(0, MAX)
      .map((x) => x.e);
    const W = viewW();
    const H = viewH();
    const v = this.v;
    foes.forEach((e, k) => {
      let w = this.els[k];
      if (!w) {
        w = { el: h('div.dg-way.danger'), arrow: h('span.dg-way-arrow', { html: CHEVRON }), txt: h('span.dg-way-txt') };
        w.el.append(w.arrow, w.txt);
        this.el.append(w.el);
        this.els[k] = w;
      }
      const el = w.el;
      setHidden(el, false);
      setText(w.txt, k === 0 ? this.label : '');
      v.copy(positions.get(e.id) ?? simToWorld(e.x, e.y)).setY(1.4).project(camera);
      const behind = v.z > 1;
      let px = (v.x * 0.5 + 0.5) * W;
      let py = (-v.y * 0.5 + 0.5) * H;
      if (behind) {
        px = W - px;
        py = H - py;
      }
      const m = 46;
      const top = W <= 640 ? 200 : 120;
      const inView = !behind && px > m && px < W - m && py > top && py < H - m;
      el.classList.toggle('edge', !inView);
      let turn = 0;
      if (!inView) {
        const cx = W / 2;
        const cy = (top + H - m) / 2;
        const dx = px - cx;
        const dy = py - cy;
        const k2 = Math.min((W / 2 - m) / Math.max(1, Math.abs(dx)), (H - m - top) / 2 / Math.max(1, Math.abs(dy)));
        px = cx + dx * k2;
        py = cy + dy * k2;
        turn = Math.atan2(dy, dx) - Math.PI / 2;
      }
      setStyle(el, 'transform', `translate(${px.toFixed(1)}px, ${py.toFixed(1)}px) translate(-50%, -50%)`);
      setStyle(w.arrow, 'transform', `rotate(${turn.toFixed(3)}rad)`);
    });
    for (let k = foes.length; k < this.els.length; k++) setHidden((this.els[k] as { el: HTMLElement }).el, true);
  }
}
